import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  parseUserIntent, 
  getDashboardQuickActions, 
  getHomepageQuickActions,
  getPageInstructions 
} from '../helper/ChatbotKeywords';
import LoginModal from './LoginModal';
import './WelcomeChatbot.css';
const MINIMIZED_KEY = 'chatbot-minimized';
const POSITION_KEY = 'chatbot-position';
/**
 * Welcome Chatbot Component
 * 
 * An interactive chatbot that helps users navigate the site
 * Appears on Homepage and Dashboard with context-specific options
 * 
 * @param {Object} props
 * @param {string} props.pageType - 'homepage' or 'dashboard'
 * @param {boolean} props.isLoggedIn - Whether user is logged in
 */
const WelcomeChatbot = ({ pageType = 'homepage', isLoggedIn = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);
  const [isMinimized, setIsMinimized] = useState(localStorage.getItem(MINIMIZED_KEY) === 'true');
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState('initial');
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const intendedPathRef = useRef(null); // Store the path user wanted to navigate to
  const messagesEndRef = useRef(null);
  
  // Drag and position state
  const [edgePosition, setEdgePosition] = useState(() => {
    return localStorage.getItem(POSITION_KEY) || 'bottom-right';
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, startX: 0, startY: 0 });
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Handle reset to initial menu
  const handleReset = useCallback(() => {
    setCurrentStep('initial');
    setIsNavigating(false);
    setMessages([{
      type: 'bot',
      text: pageType === 'dashboard'
        ? "Hi! What would you like to do?"
        : "Welcome to Snorkle! How can I help you get started?",
      timestamp: Date.now()
    }]);
  }, [pageType]);
  
  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessage = pageType === 'dashboard'
        ? "Hi! What would you like to do?"
        : "Welcome to Snorkle! How can I help you get started?";
      
      setMessages([{
        type: 'bot',
        text: welcomeMessage,
        timestamp: Date.now()
      }]);
    }
  }, [pageType, messages.length]);
  
  // Reset chat when navigating to homepage and chat is open
  useEffect(() => {
    if ((location.pathname === '/' || location.pathname === '/home') && !isMinimized) {
      handleReset();
    }
  }, [location.pathname, isMinimized, handleReset]);
  
  // Add a message to the chat
  const addMessage = (text, type = 'bot') => {
    setMessages(prev => [...prev, {
      type,
      text,
      timestamp: Date.now()
    }]);
  };
  
  // Handle user choice
  const handleChoice = (action) => {
    // Check if auth is required and user is not logged in
    if (action.requiresAuth && !isLoggedIn) {
      // Add user's choice as a message
      addMessage(action.title, 'user');
      addMessage("You need to log in or register to access this feature. Let me open the login page for you!");
      // Store the intended path for after login
      intendedPathRef.current = action.path;
      setShowLoginModal(true);
      return;
    }
    
    // Add user's choice as a message
    addMessage(action.title, 'user');
    
    switch (action.action) {
      case 'show-instructions':
        // Show detailed instructions for the current page
        const instructions = getPageInstructions(location.pathname);
        if (instructions) {
          setCurrentStep('instructions');
          setMessages(prev => [...prev, {
            type: 'instructions',
            data: instructions,
            timestamp: Date.now()
          }]);
        }
        break;
        
      case 'navigate':
        // Navigate to the specified path
        setIsNavigating(true);
        setTimeout(() => {
          navigate(action.path);
        }, 500);
        
        // Add page description if available
        if (action.usageDescription) {
          addMessage(action.usageDescription);
        } else {
          addMessage(`Great! Taking you to ${action.title}...`);
        }
        break;
        
      case 'expert-mode':
        // Show expert options
        addMessage("Here are some powerful tools for creating presentations and stories:");
        setCurrentStep('expert');
        break;
        
      case 'beginner-mode':
        // Show beginner options
        addMessage("Here are some easy ways to create content:");
        setCurrentStep('beginner');
        break;
        
      case 'submenu':
        // Show submenu options
        addMessage(`Choose a specialized tool:`);
        setCurrentStep('submenu');
        setMessages(prev => [...prev, {
          type: 'submenu',
          items: action.submenu,
          timestamp: Date.now()
        }]);
        break;
        
      case 'text-input':
        // Show text input
        setShowTextInput(true);
        addMessage("Please describe what you'd like to do:");
        break;
        
      default:
        addMessage("I'm not sure how to help with that. Let me show you the options again.");
        setCurrentStep('initial');
    }
  };
  
  // Handle text input submission
  const handleTextSubmit = () => {
    if (!textInput.trim()) return;
    
    addMessage(textInput, 'user');
    setShowTextInput(false);
    
    // Parse the user's intent
    const matches = parseUserIntent(textInput);
    
    if (matches.length > 0) {
      addMessage(`I found a few options that might help:`);
      setMessages(prev => [...prev, {
        type: 'matches',
        items: matches,
        timestamp: Date.now()
      }]);
    } else {
      addMessage("I couldn't find an exact match, but here are some popular options:");
      setCurrentStep('initial');
    }
    
    setTextInput('');
  };
  
  // Handle minimize/maximize
  const handleMinimize = () => {
    localStorage.setItem(MINIMIZED_KEY, true);
    setIsMinimized(!isMinimized);
  };
  
  // Check if current page should show help button
  const shouldShowHelpButton = () => {
    const helpPages = ['/create-presentation', '/scrollytelling/builder', '/explainer'];
    return helpPages.includes(location.pathname);
  };
  
  // Get action buttons based on current step
  const getActionButtons = () => {
    if (currentStep === 'expert') {
      return [
        {
          title: 'Create a presentation',
          description: 'Slide deck with 3D visuals',
          action: 'navigate',
          path: '/create-presentation',
          icon: '▶'
        },
        {
          title: 'Build a scrolling story',
          description: 'Interactive narrative',
          action: 'navigate',
          path: '/scrollytelling/builder',
          icon: '▶'
        },
        {
          title: 'Molecular Viewer',
          description: '3D molecular visualization',
          action: 'navigate',
          path: '/molecular-viewer',
          icon: '▶'
        },
        {
          title: 'Back to main menu',
          action: 'reset',
          icon: '↩'
        }
      ];
    } else if (currentStep === 'beginner') {
      return [
        {
          title: 'Generate an explainer video',
          description: 'AI-powered video with narration',
          action: 'navigate',
          path: '/explainer',
          icon: '▶'
        },
        {
          title: 'Browse visual library',
          description: 'Explore 3D content',
          action: 'navigate',
          path: '/visuals',
          icon: '▶'
        },
        {
          title: 'Back to main menu',
          action: 'reset',
          icon: '↩'
        }
      ];
    } else if (currentStep === 'instructions') {
      // Show back button when viewing instructions
      return [
        {
          title: 'Back to main menu',
          action: 'reset',
          icon: '↩'
        }
      ];
    } else {
      // Initial step - show context-specific actions
      return pageType === 'dashboard' 
        ? getDashboardQuickActions()
        : getHomepageQuickActions(isLoggedIn);
    }
  };
  
  // Check if we should show action buttons (hide when submenu, matches, instructions, navigating, or login modal)
  const shouldShowActionButtons = () => {
    if (showTextInput || isNavigating || showLoginModal) return false;
    
    // Check if the last message is a submenu, matches, or instructions type
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === 'submenu' || lastMessage.type === 'matches' || lastMessage.type === 'instructions') {
        return false;
      }
    }
    
    return true;
  };
  
  // Drag handlers
  const handleDragStart = (e) => {
    if (!isMinimized) return;
    
    // Only preventDefault for mouse events, not touch (touch events are passive)
    if (e.type === 'mousedown') {
      e.preventDefault();
    }
    
    setIsDragging(true);
    setHasDragged(false);
    
    const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
    const clientY = e.type === 'mousedown' ? e.clientY : e.touches[0].clientY;
    
    dragStartRef.current = {
      x: clientX,
      y: clientY,
      startX: dragPosition.x || 0,
      startY: dragPosition.y || 0
    };
  };
  
  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
    
    const deltaX = clientX - dragStartRef.current.x;
    const deltaY = clientY - dragStartRef.current.y;
    
    // Mark as dragged if moved more than 5px
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      setHasDragged(true);
    }
    
    setDragPosition({
      x: dragStartRef.current.startX + deltaX,
      y: dragStartRef.current.startY + deltaY
    });
  };
  
  const handleDragEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Calculate which edge is closest
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Get current position (relative to viewport center)
    const centerX = dragPosition.x || 0;
    const centerY = dragPosition.y || 0;
    
    // Calculate distances to each edge
    const distToLeft = Math.abs(centerX + viewportWidth / 2);
    const distToRight = Math.abs(centerX - viewportWidth / 2);
    const distToBottom = Math.abs(centerY - viewportHeight / 2);
    
    // Find closest edge (excluding top)
    const minDist = Math.min(distToLeft, distToRight, distToBottom);
    
    let newEdge;
    if (minDist === distToLeft) {
      newEdge = 'left';
    } else if (minDist === distToRight) {
      newEdge = 'right';
    } else {
      newEdge = 'bottom-right'; // Default bottom position
    }
    
    setEdgePosition(newEdge);
    localStorage.setItem(POSITION_KEY, newEdge);
    setDragPosition({ x: 0, y: 0 }); // Reset drag position after snapping
  };
  
  // Add event listeners for drag
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => handleDragMove(e);
      const handleEnd = () => handleDragEnd();
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, dragPosition]);
  
  if (!isOpen) return null;
  
  // Calculate inline styles for dragging
  const getDragStyle = () => {
    if (isDragging && isMinimized) {
      return {
        transform: `translate(${dragPosition.x}px, ${dragPosition.y}px)`,
        cursor: 'grabbing'
      };
    }
    return {};
  };

  return (
    <>
    <div 
      className={`welcome-chatbot ${isMinimized ? 'minimized' : ''} ${isDragging ? 'dragging' : ''}`}
      data-edge={edgePosition}
      style={getDragStyle()}
    >
      {/* Minimized Icon */}
      {isMinimized ? (
        <div 
          className="chatbot-minimized-icon" 
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onClick={(e) => {
            // Only expand if not dragged (distinguish click from drag)
            if (!hasDragged) {
              handleMinimize();
            }
          }}
          title="Drag to reposition or click to expand"
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-content">
              <div className="chatbot-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="chatbot-title">
                <strong>Snorkle Assistant</strong>
                <span className="chatbot-status">Here to help</span>
              </div>
            </div>
            <div className="chatbot-controls">
              <button 
                className="chatbot-control-btn" 
                onClick={handleReset}
                title="Reset chat"
              >
                ↻
              </button>
              <button 
                className="chatbot-control-btn" 
                onClick={handleMinimize}
                title="Minimize"
              >
                ▼
              </button>
            </div>
          </div>
          
          {/* Messages */}
          <div className="chatbot-body">
            <div className="chatbot-messages">
            {messages.map((msg, idx) => (
              <div key={idx}>
                {msg.type === 'bot' && (
                  <div className="chatbot-message bot-message">
                    <div className="message-bubble" dangerouslySetInnerHTML={{ __html: msg.text }}></div>
                  </div>
                )}
                
                {msg.type === 'user' && (
                  <div className="chatbot-message user-message">
                    <div className="message-bubble">{msg.text}</div>
                  </div>
                )}
                
                {msg.type === 'submenu' && (
                  <div className="chatbot-submenu">
                    {msg.items.map((item, subIdx) => {
                      const needsAuth = item.requiresAuth && !isLoggedIn;
                      return (
                        <button
                          key={subIdx}
                          className={`chatbot-submenu-btn ${needsAuth ? 'requires-auth' : ''}`}
                          onClick={() => {
                            // Check if auth is required and user is not logged in
                            if (needsAuth) {
                              addMessage(item.title, 'user');
                              addMessage("You need to log in or register to access this feature. Let me open the login page for you!");
                              // Store the intended path for after login
                              intendedPathRef.current = item.path;
                              setShowLoginModal(true);
                              return;
                            }
                            
                            addMessage(item.title, 'user');
                            setIsNavigating(true);
                            setTimeout(() => navigate(item.path), 500);
                            
                            // Add page description if available
                            if (item.usageDescription) {
                              addMessage(item.usageDescription);
                            } else {
                              addMessage(`Taking you to ${item.title}...`);
                            }
                          }}
                        >
                          {needsAuth && (
                            <div className="auth-ribbon">
                              Log in
                            </div>
                          )}
                          <strong>{item.title}</strong>
                          <span>{item.description}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {msg.type === 'matches' && (
                  <div className="chatbot-matches">
                    {msg.items.map((match, matchIdx) => (
                      <button
                        key={matchIdx}
                        className="chatbot-match-btn"
                        onClick={() => {
                          addMessage(match.title, 'user');
                          setIsNavigating(true);
                          setTimeout(() => navigate(match.path), 500);
                          addMessage(`Taking you to ${match.title}...`);
                        }}
                      >
                        <strong>{match.title}</strong>
                        <span>{match.description}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {msg.type === 'instructions' && (
                  <div className="chatbot-instructions">
                    <div className="instructions-header">
                      <h3>{msg.data.title}</h3>
                    </div>
                    <div className="instructions-steps">
                      <strong>Steps:</strong>
                      <ol>
                        {msg.data.steps.map((step, stepIdx) => (
                          <li key={stepIdx}>{step}</li>
                        ))}
                      </ol>
                    </div>
                    {msg.data.tips && msg.data.tips.length > 0 && (
                      <div className="instructions-tips">
                        <strong>Tips:</strong>
                        <ul>
                          {msg.data.tips.map((tip, tipIdx) => (
                            <li key={tipIdx} dangerouslySetInnerHTML={{ __html: tip }}></li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Action Buttons */}
          {shouldShowActionButtons() && (
            <div className="chatbot-actions">
              {getActionButtons().map((action, idx) => {
                const needsAuth = action.requiresAuth && !isLoggedIn;
                return (
                  <button
                    key={idx}
                    className={`chatbot-action-btn ${needsAuth ? 'requires-auth' : ''}`}
                    onClick={() => {
                      if (action.action === 'reset') {
                        handleReset();
                      } else {
                        handleChoice(action);
                      }
                    }}
                  >
                    {needsAuth && (
                      <div className="auth-ribbon">
                        Log in
                      </div>
                    )}
                    <span className="action-icon">{action.icon}</span>
                    <div className="action-content">
                      <strong>{action.title}</strong>
                      {action.description && <span>{action.description}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          
          {/* Text Input */}
          {showTextInput && (
            <div className="chatbot-input-container">
              <input
                type="text"
                className="chatbot-text-input"
                placeholder="Type what you'd like to do..."
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleTextSubmit()}
                autoFocus
              />
              <button 
                className="chatbot-send-btn"
                onClick={handleTextSubmit}
                disabled={!textInput.trim()}
              >
                Send
              </button>
            </div>
          )}
          
          {/* Help Button - only on specific pages */}
          {shouldShowHelpButton() && currentStep !== 'instructions' && (
            <div className="chatbot-help-container">
              <button
                className="chatbot-help-btn"
                onClick={() => handleChoice({
                  title: 'Need help getting started?',
                  action: 'show-instructions'
                })}
              >
                <span className="help-icon">?</span>
                <span>Need help getting started?</span>
              </button>
            </div>
          )}
          </div>
        </>
      )}
    </div>
    <LoginModal
      isOpen={showLoginModal}
      redirectPath={intendedPathRef.current}
      onClose={() => {
        setShowLoginModal(false);
        // Clear the intended path
        intendedPathRef.current = null;
        // Reset chatbot to initial state when modal is closed
        handleReset();
      }}
    />
    </>
  );
};

export default WelcomeChatbot;

